import React, { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { 
  InformationCircleIcon,
  FireIcon,
  ExclamationTriangleIcon,
  HandRaisedIcon,
  ChartPieIcon
} from '@heroicons/react/24/outline';
import { formatCurrency } from '../../../utils';
import MetricCard from '../../common/MetricCard';
import { useCustomerMetrics } from '../../../hooks/finance/useCustomerMetrics';

// Multi-color gradient function
const createMultiColorGradient = (colors, percentage, maxPercentage) => {
  const position = percentage / maxPercentage; // 0 to 1
  
  if (colors.length === 0) return '#000000';
  if (colors.length === 1) return colors[0];
  
  // Determine which two colors to interpolate between
  const segmentSize = 1 / (colors.length - 1);
  const segmentIndex = Math.min(Math.floor(position / segmentSize), colors.length - 2);
  const segmentPosition = (position - (segmentIndex * segmentSize)) / segmentSize;
  
  const startColor = hexToRgb(colors[segmentIndex]);
  const endColor = hexToRgb(colors[segmentIndex + 1]);
  
  // Interpolate between the two colors
  const r = Math.round(startColor.r + (endColor.r - startColor.r) * segmentPosition);
  const g = Math.round(startColor.g + (endColor.g - startColor.g) * segmentPosition);
  const b = Math.round(startColor.b + (endColor.b - startColor.b) * segmentPosition);
  
  return `rgb(${r}, ${g}, ${b})`;
};

const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

// Separate color schemes for retainer and project customers
// RETAINER COLOR SCHEME - Change this for retainer clients
const RETAINER_COLOR_SCHEME = ["#f38bae", "#ED145B"]; // Pink gradient

// PROJECT COLOR SCHEME - Change this for project clients
const PROJECT_COLOR_SCHEME = ["#87b3c5", "#0093CF"]; // Blue gradient

// Custom tooltip component
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
        <p className="font-semibold text-gray-900">{data.name}</p>
        {data.customerType && (
          <p className="text-xs text-gray-500 capitalize">
            {data.customerType}
          </p>
        )}
        <p className="text-sm text-gray-600">
          Revenue: ${data.value.toLocaleString()}
        </p>
        <p className="text-sm text-gray-600">
          Percentage: {data.percentage.toFixed(1)}%
        </p>
      </div>
    );
  }
  return null;
};

// Custom legend component
const CustomLegend = ({ payload }) => {
  return (
    <div className="space-y-2 max-h-80 overflow-y-auto pr-2">
      {payload.map((entry, index) => (
        <div key={index} className="flex items-center justify-between hover:bg-gray-50 p-1 rounded">
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            <div 
              className="w-4 h-4 rounded-full flex-shrink-0"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-sm text-gray-700 truncate">{entry.value}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

export const ClientMix = ({ 
  customerData = [],
  totalCustomers = null,
  retainerCustomers = null,
  projectCustomers = null,
  dateRange = null,
  loading = false,
  error = null,
  selectedYear = null
}) => {
  // Use the customer metrics hook
  const {
    ltvData,
    churnData,
    loading: metricsLoading,
  } = useCustomerMetrics(dateRange);


  // Process data for the chart
  const chartData = useMemo(() => {
    if (!customerData || customerData.length === 0) {
      return [];
    }

    // Calculate max percentage for each customer type separately
    const retainerCustomers = customerData.filter(c => c.customer_type === 'retainer');
    const projectCustomers = customerData.filter(c => c.customer_type === 'project');
    
    // console.log("retainerCustomers===",retainerCustomers)

    const maxRetainerPercentage = retainerCustomers.length > 0 
      ? Math.max(...retainerCustomers.map(c => c.percentage_of_total))
      : 0;
    const maxProjectPercentage = projectCustomers.length > 0
      ? Math.max(...projectCustomers.map(c => c.percentage_of_total))
      : 0;

    return customerData.map((customer, index) => {
      
      // Select color scheme based on customer type
      const colorScheme = customer.customer_type === 'retainer' 
        ? RETAINER_COLOR_SCHEME 
        : PROJECT_COLOR_SCHEME;
      
      const maxPercentage = customer.customer_type === 'retainer'
        ? maxRetainerPercentage
        : maxProjectPercentage;

      
      return {
        name: customer.customer_name,
        value: customer.total_revenue,
        percentage: customer.percentage_of_total,
        customerType: customer.customer_type,
        color: createMultiColorGradient(colorScheme, customer.percentage_of_total, maxPercentage),
        isOther: customer.is_other_category || false
      };
    });
  }, [customerData]);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    if (chartData.length === 0) return null;

    const totalRevenue = chartData.reduce((sum, item) => sum + item.value, 0);
    const topCustomer = chartData.find(item => !item.isOther) || chartData[0];
    const regularCustomers = chartData.filter(item => !item.isOther);

    return {
      totalRevenue,
      topCustomer,
      customerCount: regularCustomers.length,
      hasOtherCategory: chartData.some(item => item.isOther)
    };
  }, [chartData]);

  // Calculate concentration risk
  const concentrationRisk = useMemo(() => {
    if (!customerData || customerData.length === 0) return null;
    
    const regularCustomers = customerData.filter(
      customer => !customer.is_other_category
    );
    
    const sortedByPercentage = [...regularCustomers].sort(
      (a, b) => b.percentage_of_total - a.percentage_of_total
    );
    
    const top2Sum = sortedByPercentage
      .slice(0, 2)
      .reduce((sum, customer) => sum + customer.percentage_of_total, 0);
    
    return top2Sum;
  }, [customerData]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <MetricCard key={i} loading={true} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        <ExclamationTriangleIcon className="h-5 w-5 inline mr-2" />
        Error loading customer data: {error}
        <button 
          onClick={() => window.location.reload()}
          className="ml-4 text-sm underline hover:text-red-900"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!chartData || chartData.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
        <ChartPieIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-500">No customer revenue data available for {selectedYear}</p>
      </div>
    );
  }

  return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
        <div className="lg:col-span-1 space-y-4">
          <MetricCard
            title="Long Term Value (LTV)"
            value={ltvData.ltv > 0 ? formatCurrency(ltvData.ltv) : 'TBD'}
            // change={ltvData.change}
            changeLabel=''
            icon={InformationCircleIcon}
            loading={metricsLoading}
            isClickable={false}
            inverseChangeColor={false}
          />

          <MetricCard
            title="Churn Rate"
            value={churnData.churnRate > 0 ? `${churnData.churnRate.toFixed(1)}%` : 'TBD'}
            // change={churnData.change}
            changeLabel=''
            icon={FireIcon}
            loading={metricsLoading}
            isClickable={false}
            inverseChangeColor={true}
          />

          <MetricCard
            title="Concentration Risk (Top 2)"
            value={concentrationRisk !== null ? `${concentrationRisk.toFixed(1)}%` : 'N/A'}
            // change={0}
            changeLabel=""
            icon={HandRaisedIcon}
            loading={false}
            isClickable={false}
            inverseChangeColor={true}
          />
        </div>

        {/* Right Column - Pie Chart */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow p-6 h-full">
            <div className="mb-4">
              <h3 className="text-lg font-medium text-gray-900">Revenue Mix</h3>
              <p className="text-sm text-gray-600 mt-1">
                Total {summaryStats?.customerCount || 0} customers/projects for {selectedYear}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Pie Chart */}
              <div className="flex items-center justify-center">
                <div className="w-80 h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={80}
                        outerRadius={140}
                        paddingAngle={2}
                        dataKey="value"
                        stroke="#ffffff"
                        strokeWidth={2}
                      >
                        {chartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Legend */}
              <div className="flex flex-col justify-center">
                <CustomLegend payload={chartData.map(item => ({
                  value: item.name,
                  color: item.color,
                  payload: item
                }))} />
              </div>
            </div>

            {/* Summary Stats */}
            {summaryStats && (
              <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-200">
                <div className="text-center">
                  <div className="text-lg font-semibold text-gray-900">
                    {retainerCustomers !== null ? retainerCustomers : '-'}
                  </div>
                  <div className="text-sm text-gray-500">Total Retainers</div>
                </div>
                
                <div className="text-center">
                  <div className="text-lg font-semibold text-gray-900">
                    {projectCustomers !== null ? projectCustomers : '-'}
                  </div>
                  <div className="text-sm text-gray-500">Total Projects</div>
                </div>
                  
                <div className="text-center">
                  <div className="text-lg font-semibold text-gray-900">
                    {summaryStats.topCustomer.percentage.toFixed(1)}%
                  </div>
                  <div className="text-sm text-gray-500">Top Share</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
  );
}